// AI with your own key (M15): the only code in the app that talks to a server, and only to the two providers'
// API hosts (src/app/csp.ts), only after a key is set. Every function first checks the key and throws
// AiError('no-key') without touching the network otherwise.
//   smartFix   — a small JPEG of the photo → slider values for existing settings (never generated pixels).
//   adText     — dish name, short description and promo line in English and Spanish (usted).
//   aiImage    — "touch-up" / "upscale" through the provider's image model; the UI always shows the result
//                beside the original, labelled AI-generated (delivery apps require photos of the real dish).
import { AI_HOSTS } from '../../app/csp';
import { loadAi, type AiSettings } from './aiSettings';

export class AiError extends Error {
  constructor(
    readonly code: 'no-key' | 'bad-key' | 'quota' | 'network' | 'bad-answer' | 'refused',
    message: string = code,
  ) {
    super(message);
    this.name = `AiError:${code}`;
  }
}

/** Tests replace fetch; the app uses the browser's. */
let fetchImpl: typeof fetch = (...a) => fetch(...a);
export function setAiFetch(f: typeof fetch | null) {
  fetchImpl = f ?? ((...a) => fetch(...a));
}

async function settings(): Promise<AiSettings> {
  const s = await loadAi();
  if (!s?.key) throw new AiError('no-key', 'No AI key is set');
  return s;
}

async function call(url: string, init: RequestInit): Promise<unknown> {
  if (!AI_HOSTS.some((h) => url.startsWith(h + '/'))) throw new AiError('refused', 'host not allowed');
  let res: Response;
  try {
    res = await fetchImpl(url, init);
  } catch {
    throw new AiError('network', 'Could not reach the AI provider');
  }
  if (res.status === 401 || res.status === 403)
    throw new AiError('bad-key', `The provider refused the key (HTTP ${res.status})`);
  if (res.status === 429)
    throw new AiError('quota', 'The provider says the quota or rate limit is reached (HTTP 429)');
  if (!res.ok) throw new AiError('network', `The provider answered HTTP ${res.status}`);
  return res.json();
}

export const b64 = (u: Uint8Array) => {
  let s = '';
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode(...u.subarray(i, i + 0x8000));
  return btoa(s);
};
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

type Schema = {
  type: 'object';
  properties: Record<string, unknown>;
  required: string[];
  additionalProperties: false;
};

/** One JSON answer from the vision/text model (image optional). */
async function askJson(
  s: AiSettings,
  prompt: string,
  schema: Schema,
  image?: { bytes: Uint8Array; mime: string },
): Promise<Record<string, unknown>> {
  let text: string | undefined;
  if (s.provider === 'openai') {
    const content: unknown[] = [{ type: 'text', text: prompt }];
    if (image)
      content.push({
        type: 'image_url',
        image_url: { url: `data:${image.mime};base64,${b64(image.bytes)}` },
      });
    const r = (await call('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.key}` },
      body: JSON.stringify({
        model: s.models.openai.vision,
        messages: [{ role: 'user', content }],
        response_format: { type: 'json_schema', json_schema: { name: 'answer', strict: true, schema } },
      }),
    })) as { choices?: { message?: { content?: string } }[] };
    text = r.choices?.[0]?.message?.content;
  } else {
    const parts: unknown[] = [{ text: prompt }];
    if (image) parts.push({ inline_data: { mime_type: image.mime, data: b64(image.bytes) } });
    const { additionalProperties: _a, ...gSchema } = schema;
    const r = (await call(
      `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(s.models.google.vision)}:generateContent`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-goog-api-key': s.key },
        body: JSON.stringify({
          contents: [{ parts }],
          generationConfig: { responseMimeType: 'application/json', responseSchema: gSchema },
        }),
      },
    )) as { candidates?: { content?: { parts?: { text?: string }[] } }[] };
    text = r.candidates?.[0]?.content?.parts?.find((p) => p.text)?.text;
  }
  if (!text) throw new AiError('bad-answer', 'The AI answer was empty');
  try {
    const v = JSON.parse(text) as unknown;
    if (!v || typeof v !== 'object') throw new Error();
    return v as Record<string, unknown>;
  } catch {
    throw new AiError('bad-answer', 'The AI answer was not valid JSON');
  }
}

// ───────────── (a) Smart fix ─────────────

/** The sliders smart fix may set, with their ranges (same as the Pro panels). */
export const SMART_FIX_RANGES = {
  exposure: [-3, 3],
  contrast: [-100, 100],
  highlights: [-100, 100],
  shadows: [-100, 100],
  whites: [-100, 100],
  blacks: [-100, 100],
  temp: [-100, 100],
  tint: [-100, 100],
  vibrance: [-100, 100],
  saturation: [-100, 100],
  cleanWhites: [0, 100],
} as const;
export type SmartFix = { -readonly [K in keyof typeof SMART_FIX_RANGES]: number };

const SMART_FIX_PROMPT = `You are a food photo retoucher. Look at this restaurant food photo and choose slider values that make it look appetizing, natural and well exposed, as a professional food photographer would. Values are ABSOLUTE settings from neutral (0). Ranges: exposure in EV -3..3; contrast, highlights, shadows, whites, blacks, temp (+ warmer), tint (+ magenta), vibrance, saturation -100..100; cleanWhites 0..100 (neutralizes white plates and backgrounds). Prefer small, believable changes; never change the food's real colours. Answer only the JSON.`;

/** Validates and clamps a smart-fix answer; missing or non-numeric values become 0. */
export function parseSmartFix(v: Record<string, unknown>): SmartFix {
  const out = {} as SmartFix;
  for (const [k, [lo, hi]] of Object.entries(SMART_FIX_RANGES) as [
    keyof SmartFix,
    readonly [number, number],
  ][]) {
    const n = Number(v[k]);
    out[k] = Number.isFinite(n)
      ? Math.min(hi, Math.max(lo, k === 'exposure' ? Math.round(n * 100) / 100 : Math.round(n)))
      : 0;
  }
  return out;
}

export async function smartFix(jpeg: Uint8Array): Promise<SmartFix> {
  const s = await settings();
  const schema: Schema = {
    type: 'object',
    properties: Object.fromEntries(Object.keys(SMART_FIX_RANGES).map((k) => [k, { type: 'number' }])),
    required: Object.keys(SMART_FIX_RANGES),
    additionalProperties: false,
  };
  return parseSmartFix(await askJson(s, SMART_FIX_PROMPT, schema, { bytes: jpeg, mime: 'image/jpeg' }));
}

// ───────────── (b) Ad text ─────────────

export type AdTextAnswer = {
  dishName: { en: string; es: string };
  description: { en: string; es: string };
  promo: { en: string; es: string };
};

const AD_TEXT_PROMPT = (dish: string) =>
  `You write menu and social-media text for a restaurant in Guatemala. ${dish ? `The dish is called "${dish}". ` : ''}${'Look at the photo if there is one. '}Write: a dish name (max 40 characters), a short appetizing description (max 90 characters, ingredients first, no prices), and a short promo line (max 40 characters). Write each in English and in Latin American neutral Spanish. Spanish must address the customer with "usted" (e.g. "Pruébelo", "Pida ya"), never "tú" or "vos". No emoji, no hashtags, no prices, no claims you cannot see. Answer only the JSON.`;

const clip = (s: unknown, n: number) =>
  typeof s === 'string' ? s.replace(/\s+/g, ' ').trim().slice(0, n) : '';

export function parseAdText(v: Record<string, unknown>): AdTextAnswer {
  const pair = (k: string, n: number) => {
    const o = (v[k] ?? {}) as Record<string, unknown>;
    return { en: clip(o.en, n), es: clip(o.es, n) };
  };
  const out = {
    dishName: pair('dishName', 60),
    description: pair('description', 140),
    promo: pair('promo', 60),
  };
  if (!out.dishName.es && !out.dishName.en) throw new AiError('bad-answer', 'The AI answer had no dish name');
  return out;
}

export async function adText(o: {
  dishName?: string;
  photo?: { bytes: Uint8Array; mime: string };
}): Promise<AdTextAnswer> {
  const s = await settings();
  const pairSchema = {
    type: 'object',
    properties: { en: { type: 'string' }, es: { type: 'string' } },
    required: ['en', 'es'],
    additionalProperties: false,
  };
  const schema: Schema = {
    type: 'object',
    properties: { dishName: pairSchema, description: pairSchema, promo: pairSchema },
    required: ['dishName', 'description', 'promo'],
    additionalProperties: false,
  };
  if (s.provider === 'google')
    for (const p of Object.values(schema.properties) as Record<string, unknown>[])
      delete p.additionalProperties;
  return parseAdText(await askJson(s, AD_TEXT_PROMPT(o.dishName ?? ''), schema, o.photo));
}

// ───────────── (c) AI touch-up / upscale ─────────────

const IMAGE_PROMPTS = {
  touchup:
    'Retouch this restaurant food photo: better light and white balance, remove small crumbs and smudges on the plate, keep the exact same dish, ingredients, portion, plate and composition. Do not add or remove any food. Photorealistic.',
  upscale:
    'Recreate this exact food photo at higher resolution with finer, sharper detail. Keep everything identical: same dish, ingredients, portion, plate, framing and colours. Do not add or remove anything. Photorealistic.',
};

/** Sends the photo to the provider's image model; returns the generated image bytes (PNG/JPEG). */
export async function aiImage(
  kind: 'touchup' | 'upscale',
  photo: { bytes: Uint8Array; mime: string },
): Promise<{ bytes: Uint8Array; mime: string }> {
  const s = await settings();
  if (s.provider === 'openai') {
    const fd = new FormData();
    fd.append('model', s.models.openai.image);
    fd.append('prompt', IMAGE_PROMPTS[kind]);
    fd.append('size', 'auto');
    fd.append(
      'image',
      new Blob([photo.bytes as Uint8Array<ArrayBuffer>], { type: photo.mime }),
      photo.mime === 'image/png' ? 'photo.png' : 'photo.jpg',
    );
    const r = (await call('https://api.openai.com/v1/images/edits', {
      method: 'POST',
      headers: { Authorization: `Bearer ${s.key}` },
      body: fd,
    })) as { data?: { b64_json?: string }[] };
    const d = r.data?.[0]?.b64_json;
    if (!d) throw new AiError('bad-answer', 'The AI returned no image');
    return { bytes: unb64(d), mime: 'image/png' };
  }
  const r = (await call(
    `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(s.models.google.image)}:generateContent`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-goog-api-key': s.key },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              { text: IMAGE_PROMPTS[kind] },
              { inline_data: { mime_type: photo.mime, data: b64(photo.bytes) } },
            ],
          },
        ],
        generationConfig: { responseModalities: ['IMAGE', 'TEXT'] },
      }),
    },
  )) as {
    candidates?: {
      content?: {
        parts?: {
          inlineData?: { data?: string; mimeType?: string };
          inline_data?: { data?: string; mime_type?: string };
        }[];
      };
    }[];
  };
  const part = r.candidates?.[0]?.content?.parts?.find((p) => p.inlineData?.data || p.inline_data?.data);
  const data = part?.inlineData?.data ?? part?.inline_data?.data;
  if (!data) throw new AiError('bad-answer', 'The AI returned no image');
  return {
    bytes: unb64(data),
    mime: part?.inlineData?.mimeType ?? part?.inline_data?.mime_type ?? 'image/png',
  };
}
